import { isLocalAudio, type SentenceAudioBlock } from "@/utils/audioBlockMap";
import { canonicalJson } from "@/utils/cachedPlayback";
import { estimateSpeechDurationSeconds } from "@/utils/playbackBuffering";
import { audioSourceBlockIds } from './cachedAudioBlocks';

export interface AudioPreparationItem {
  id: string;
  key: string;
  blockId: string;
  sentence: SentenceAudioBlock;
}

export const audioPreparationKey = (voiceId: string, revision: string, text: string,
  synthesisOptions: Record<string, unknown>) => canonicalJson({
  voice: voiceId, revision, text, options: synthesisOptions,
});

// Positions and formatting are not audio inputs. Actor revisions include saved
// reference, calibration and synthesis-setting changes made by administrators.
export const audioPreparationItems = (
  sentences: SentenceAudioBlock[], revisions: ReadonlyMap<string, string>,
): AudioPreparationItem[] => sentences.filter(sentence => !isLocalAudio(sentence) && revisions.has(sentence.voiceId)).map(sentence => ({
  id: sentence.id, blockId: sentence.sourceBlockId, sentence,
  key: audioPreparationKey(sentence.voiceId, revisions.get(sentence.voiceId)!, sentence.text, sentence.synthesisOptions),
}));

export function changedAudioKeys(
  previous: ReadonlyMap<string, string> | null, items: AudioPreparationItem[],
): Set<string> {
  return new Set(previous === null ? [] : items
    .filter(item => previous.get(item.id) !== item.key).map(item => item.key));
}

export function planAudioPreparation(
  items: AudioPreparationItem[], visibleBlocks: ReadonlySet<string>,
  activeBlock: string, dirty: ReadonlySet<string>,
): AudioPreparationItem[] {
  const nearby = items.filter(item => audioSourceBlockIds(item.sentence).some(id => visibleBlocks.has(id)));
  const start = nearby[0] ?? items.find(item => audioSourceBlockIds(item.sentence).includes(activeBlock));
  const priorities: AudioPreparationItem[] = [];
  if (start) {
    let seconds = 0;
    for (let index = items.indexOf(start); index < items.length && priorities.length < 12; index++) {
      const item = items[index];
      priorities.push(item);
      seconds += estimateSpeechDurationSeconds(item.sentence.text);
      if (seconds >= 30) break;
    }
  }
  priorities.push(...items.filter(item => dirty.has(item.key)));
  // The viewport is a priority, not a coverage limit. Drain every remaining
  // sentence using the same bounded worker, including on initial document load.
  priorities.push(...items);
  return [...new Map(priorities.map(item => [item.key, item])).values()];
}

/** Replaceable speculative queue: one request at a time, never a queue per edit.
 * An already running request may finish; only the latest waiting tasks survive.
 * Foreground playback uses its own bounded queue and joins matching requests.
 */
export class AudioPreparationQueue<T extends { key: string }> {
  private waiting: T[] = [];
  private running = false;
  private paused = true;
  constructor(private readonly run: (item: T) => Promise<void>) {}

  replace(items: T[]) {
    this.waiting = [...items];
    this.paused = false;
    void this.drain();
  }

  pause() { this.paused = true; this.waiting = []; }

  private async drain() {
    if (this.running) return;
    this.running = true;
    try {
      while (!this.paused && this.waiting.length) {
        const item = this.waiting.shift()!;
        // Callers report errors; a failed item must not become an unhandled rejection.
        try { await this.run(item); } catch { /* next explicit queue plan may retry */ }
      }
    } finally { this.running = false; }
  }
}
